/**
 * 收货地址本地缓存
 *
 * 用 localStorage 而非 sessionStorage: 微信 WebView 从聊天窗重新进入常是新
 * session, sessionStorage 会丢; 收货地址是跨会话的稳定偏好.
 * 代价是必须显式清理 —— 退出登录 / 删除地址 时调 clearAddressCache().
 *
 * 缓存只是首屏渲染用的快照, addressId 是否有效一律由服务端校验决定
 * (见 store/modules/payment/actions/address.js).
 */
import { getMainPlatform } from './auth'

const CACHE_KEY = 'user_address_cache'
const CACHE_VERSION = 1
// 超过该时长的缓存直接丢弃, 避免长期不用的设备留着很旧的地址
const MAX_AGE = 30 * 24 * 60 * 60 * 1000

const EMPTY_INFO = {}

const isPlainObject = value =>
  Object.prototype.toString.call(value) === '[object Object]'

/**
 * 读取缓存
 * @returns {{ addressId: string, addressInfo: Object } | null} 无有效缓存返回 null
 */
export function readAddressCache() {
  let raw = ''

  try {
    raw = localStorage.getItem(CACHE_KEY) || ''
  } catch (error) {
    // 隐私模式下 localStorage 可能不可用
    console.error('读取地址缓存失败', error)
    return null
  }

  if (!raw) return null

  let cache = null

  try {
    cache = JSON.parse(raw)
  } catch (error) {
    console.error('地址缓存解析失败, 已清除', error)
    clearAddressCache()
    return null
  }

  if (!isPlainObject(cache) || !cache.addressId) return null

  // 版本 / 平台 / 时效校验, 任一不通过都视为无缓存
  const isStale =
    cache.version !== CACHE_VERSION ||
    cache.platformId !== getMainPlatform() ||
    !cache.updatedAt ||
    Date.now() - cache.updatedAt > MAX_AGE

  if (isStale) {
    clearAddressCache()
    return null
  }

  return {
    addressId: String(cache.addressId),
    addressInfo: isPlainObject(cache.addressInfo)
      ? cache.addressInfo
      : EMPTY_INFO
  }
}

/**
 * 写入缓存
 *
 * addressInfo 为空且 addressId 未变化时, 保留已有的 addressInfo,
 * 避免只更新 id 的旧调用方(payment2)把详情擦掉.
 *
 * @param {Object} params
 * @param {string|number} params.addressId
 * @param {Object} [params.addressInfo]
 */
export function writeAddressCache({ addressId, addressInfo } = {}) {
  if (!addressId) return

  const id = String(addressId)
  const hasInfo =
    isPlainObject(addressInfo) &&
    Object.keys(addressInfo).length !== 0

  let nextInfo = hasInfo ? addressInfo : EMPTY_INFO

  if (!hasInfo) {
    const prev = readAddressCache()
    if (prev && prev.addressId === id) {
      nextInfo = prev.addressInfo
    }
  }

  try {
    localStorage.setItem(
      CACHE_KEY,
      JSON.stringify({
        version: CACHE_VERSION,
        platformId: getMainPlatform(),
        addressId: id,
        addressInfo: nextInfo,
        updatedAt: Date.now()
      })
    )
  } catch (error) {
    // 超出配额等情况不应阻断主流程
    console.error('写入地址缓存失败', error)
  }
}

/**
 * 清除缓存 —— 退出登录、删除当前选中地址时必须调用
 */
export function clearAddressCache() {
  try {
    localStorage.removeItem(CACHE_KEY)
  } catch (error) {
    console.error('清除地址缓存失败', error)
  }
}
